import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	InputLabel,
	Radio,
	RadioGroup,
	Switch,
	TextField,
	Typography
} from "@mui/material";
import React, { FC, useEffect, useMemo, useState } from "react";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AutoDeleteIcon from "@mui/icons-material/AutoDelete";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import SyncIcon from "@mui/icons-material/Sync";

import "../configuration.css";
import ConfirmationDialog from "../../../common/ConfirmationDialog";
import {
	useLazyPingPongQuery,
	useRebootInferenceMutation,
	useRebootSystemMutation,
	useResetSystemMutation
} from "../../../redux/reducers/configuration.reducer";
import { FieldValues, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { clearAuthState, updateToken } from "../../../redux/reducers/auth.reducer";
import { LoadingButton } from "@mui/lab";
import authServices from "../../../redux/services/auth.services";
import {
	useForceSyncMutation,
	useGetClientsListQuery,
	useGetNodesListQuery,
	useGetSiteConfigQuery,
	useUpdateSiteConfigMutation
} from "../../../redux/reducers/settings.reducer";
import { RESTRICTED_ACCESS_E, getRestrictedAccessList } from "../../../utils/commonUtils";
import NumberInput from "../../../common/NumberInput";
import { CAMERA_MODE_PROTOCOL_E } from "../../../utils/constants";
import { useNavigate } from "react-router-dom";
import { showFeedbackNotification } from "../../../redux/reducers/notifications.reducer";
import ClientNodeDetailsDialog, { IClientNodeDetails } from "./ClientNodeDetailsDialog";
import { useNestedPageLayoutContext } from "../../../router/NestedPageLayout";

const REBOOT_SYSTEM_LOADER_MESSAGE = "The device is restarting. Please wait...";
const REBOOT_INFERENCE_LOADER_MESSAGE = "The inference system is restarting. Please wait...";

const DEFALT_PROCESSING_WIDTH = 1920;
const DEFALT_PROCESSING_HEIGHT = 1080;

interface IMaintenanceSettings {
	drawBoundingBox: boolean;
	showVehicleCount: boolean;
	enableFuzzyMatching: boolean;
	fuzzyMatchingThreshold: string;
	productName: string;
	enableHttpWebhook: boolean;
	httpWebhookUrl: string;
	sendOneEvent: boolean;
	protocol: "tcp" | "udp";
	unauthorizedExit: boolean;
	filterUnreadableEvents: boolean;
	webhookAttachFrame: boolean;
	webhookAttachPlate: boolean;
	webhookCustomHeaders: { key: string; value: string }[];
	enableTcp: boolean;
	tcpAttachFrame: boolean;
	tcpAttachPlate: boolean;
	client_id: string;
	client_name: string;
	node_id: string;
	node_name: string;
	processing_width: string;
	processing_height: string;
}

const Maintenance: FC = () => {
	const { forceSyncStatusLoading, startSyncStatusPolling } = useNestedPageLayoutContext();

	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const authState = useAppSelector((state) => state.auth);

	const [confirmationDialogType, setConfirmationDialogType] = useState<
		"reboot_device" | "reboot_inference" | "reset" | "force_sync" | null
	>(null);
	const [loaderDialogMessage, setLoaderDialogMessage] = useState<string>("");
	const [showResetAuthDialog, setShowResetAuthDialog] = useState<boolean>(false);
	const [showAuthLoader, setShowAuthLoader] = useState<boolean>(false);
	const [authenticationError, setAuthenticationError] = useState<string>("");
	const [showClientNodeDetailsDialog, setShowClientNodeDetailsDialog] = useState<boolean>(false);
	// const [testWebhookLoading, setTestWebhookLoading] = useState<boolean>(false);

	const [maintenanceSettings, setMaintenanceSettings] = useState<IMaintenanceSettings>({
		drawBoundingBox: false,
		showVehicleCount: false,
		enableFuzzyMatching: false,
		fuzzyMatchingThreshold: "",
		productName: "",
		enableHttpWebhook: false,
		httpWebhookUrl: "",
		sendOneEvent: false,
		protocol: "tcp",
		unauthorizedExit: false,
		filterUnreadableEvents: false,
		webhookAttachFrame: false,
		webhookAttachPlate: false,
		webhookCustomHeaders: [],
		enableTcp: false,
		tcpAttachFrame: false,
		tcpAttachPlate: false,
		client_id: "",
		client_name: "",
		node_id: "",
		node_name: "",
		processing_width: String(DEFALT_PROCESSING_WIDTH),
		processing_height: String(DEFALT_PROCESSING_HEIGHT)
	});

	const clientNodeDetails = useMemo<IClientNodeDetails>(
		() => ({
			clientId: maintenanceSettings.client_id,
			clientName: maintenanceSettings.client_name,
			nodeId: maintenanceSettings.node_id,
			nodeUsername: maintenanceSettings.node_name
		}),
		[
			maintenanceSettings.client_id,
			maintenanceSettings.client_name,
			maintenanceSettings.node_id,
			maintenanceSettings.node_name
		]
	);

	const [rebootSystem] = useRebootSystemMutation();
	const [pingPong] = useLazyPingPongQuery({});
	const [resetSystem, { isSuccess: resetSystemSuccess, error: resetSystemError, reset: resetResetSystemState }] =
		useResetSystemMutation();
	const [
		rebootInference,
		{ error: rebootInferenceError, isSuccess: rebootInferenceSuccess, reset: resetRebootInferenceState }
	] = useRebootInferenceMutation();

	const { data: siteConfig, refetch: refetchSiteConfig } = useGetSiteConfigQuery(null, {
		skip: !authState.user.token || authState.user.token === "invalid"
	});

	const [
		updateSiteConfig,
		{
			isLoading: updateSiteConfigLoading,
			isSuccess: updateSiteConfigSuccess,
			error: updateSiteConfigError,
			reset: resetUpdateSiteConfigState
		}
	] = useUpdateSiteConfigMutation();

	// GET CLIENTS LIST
	const {
		data: getClientsListResponse,
		isLoading: getClientsListLoading,
		isFetching: getClientsListFetching,
		refetch: refetchClientsList
	} = useGetClientsListQuery();

	// GET NODES LIST
	const {
		data: getNodesListResponse,
		isLoading: getNodesListLoading,
		isFetching: getNodesListFetching,
		refetch: refetchNodesList
	} = useGetNodesListQuery();

	// FORCE SYNC
	const [
		forceSync,
		{ data: forceSyncResponse, isLoading: forceSyncLoading, error: forceSyncError, reset: resetForceSyncState }
	] = useForceSyncMutation();

	const clientDetails = useMemo(
		() => (getClientsListResponse ? getClientsListResponse[0] : null),
		[getClientsListResponse]
	);

	const nodeDetails = useMemo(() => (getNodesListResponse ? getNodesListResponse[0] : null), [getNodesListResponse]);

	const RestrictedAccess = useMemo<RESTRICTED_ACCESS_E[]>(
		() => getRestrictedAccessList(siteConfig?.cloud_sync_enabled ?? true),
		[siteConfig]
	);

	const initialMaintenanceSettings = useMemo<IMaintenanceSettings>(
		() => ({
			drawBoundingBox: siteConfig?.ml_settings.draw_bbox ?? false,
			showVehicleCount: siteConfig?.ml_settings.show_lc_counts_rtmp ?? false,
			enableFuzzyMatching: siteConfig?.ml_settings.enable_auth_plate_mathching ?? false,
			fuzzyMatchingThreshold: siteConfig ? String(siteConfig.ml_settings.auth_plate_matching_threshold) : "",
			productName: siteConfig?.product_name ?? "",
			enableHttpWebhook: siteConfig?.ml_settings.enable_manager_webhook ?? false,
			httpWebhookUrl: siteConfig?.ml_settings.manager_webhook_url ?? "",
			sendOneEvent: siteConfig?.ml_settings.manager_webhook_send_single_event ?? false,
			protocol: siteConfig ? (siteConfig.camera_mode === CAMERA_MODE_PROTOCOL_E.TCP ? "tcp" : "udp") : "tcp",
			unauthorizedExit: siteConfig?.ml_settings.allow_unauth_exit ?? false,
			filterUnreadableEvents: siteConfig?.filter_unreadable_plate_events ?? false,
			webhookAttachFrame: siteConfig?.ml_settings.manager_webhook_attach_frame ?? false,
			webhookAttachPlate: siteConfig?.ml_settings.manager_webhook_attach_plate ?? false,
			webhookCustomHeaders:
				siteConfig && siteConfig.ml_settings.manager_webhook_custom_header
					? Object.entries(siteConfig.ml_settings.manager_webhook_custom_header).map(([key, value]) => ({ key, value }))
					: [],
			enableTcp: siteConfig?.ml_settings.enable_manager_tcp || false,
			tcpAttachFrame: siteConfig?.ml_settings.manager_tcp_attach_frame || false,
			tcpAttachPlate: siteConfig?.ml_settings.manager_tcp_attach_plate || false,
			client_id: clientDetails && clientDetails.client_id ? String(clientDetails.client_id) : "",
			client_name: clientDetails?.name ?? "",
			node_id: nodeDetails && nodeDetails.node_id ? String(nodeDetails.node_id) : "",
			node_name: nodeDetails?.username ?? "",
			processing_width: siteConfig ? String(siteConfig.ml_settings.streammux_width) : String(DEFALT_PROCESSING_WIDTH),
			processing_height: siteConfig ? String(siteConfig.ml_settings.streammux_height) : String(DEFALT_PROCESSING_HEIGHT)
		}),
		[clientDetails, nodeDetails, siteConfig]
	);

	const maintenanceSettingsChanged = useMemo(
		() => JSON.stringify(initialMaintenanceSettings) !== JSON.stringify(maintenanceSettings),
		[initialMaintenanceSettings, maintenanceSettings]
	);

	const {
		register,
		handleSubmit,
		formState: { errors }
	} = useForm();

	function openClientNodeDetailsDialog() {
		setShowClientNodeDetailsDialog(true);
	}

	function closeClientNodeDetailsDialog() {
		setShowClientNodeDetailsDialog(false);
		refetchClientsList();
		refetchNodesList();
	}

	function handleOpenResetAuthDialog() {
		setShowResetAuthDialog(true);
	}

	function handleCloseResetAuthDialog() {
		setShowResetAuthDialog(false);
	}

	function handleOpenLoaderDialog(message: string) {
		setLoaderDialogMessage(message);
	}

	function handleCloseLoaderDialog() {
		setLoaderDialogMessage("");
	}

	function handleOpenConfirmationDialog(type: "reboot_device" | "reboot_inference" | "reset" | "force_sync") {
		setConfirmationDialogType(type);
	}

	function handleCloseConfirmationDialog() {
		setConfirmationDialogType(null);
	}

	function waitForSystemRestart() {
		const pingPongWaitTimer = setTimeout(() => {
			pingPong()
				.then((response) => {
					if (response) {
						handleCloseLoaderDialog();
						clearTimeout(pingPongWaitTimer);
					}
				})
				.catch((error) => {
					waitForSystemRestart();
				});
		}, 10000);
	}

	function handleConfirmAction() {
		if (confirmationDialogType === "reboot_device") {
			rebootSystem();
			handleOpenLoaderDialog(REBOOT_SYSTEM_LOADER_MESSAGE);
			handleCloseConfirmationDialog();
			waitForSystemRestart();
		} else if (confirmationDialogType === "reboot_inference") {
			rebootInference(null);
			handleOpenLoaderDialog(REBOOT_INFERENCE_LOADER_MESSAGE);
			handleCloseConfirmationDialog();
		} else if (confirmationDialogType === "reset") {
			handleOpenResetAuthDialog();
			handleCloseConfirmationDialog();
		} else if (confirmationDialogType === "force_sync") {
			forceSync();
			handleCloseConfirmationDialog();
		}
	}

	function handleSubmitAuthForm(data: FieldValues) {
		if (authState.user.username) {
			setShowAuthLoader(true);

			authServices
				.handleLoginUser({ username: authState.user.username, password: data.password })
				.then((response) => {
					handleOpenLoaderDialog("The device is resetting. Please wait...");
					handleCloseResetAuthDialog();
					dispatch(updateToken(response.token));
					resetSystem(response.token);
				})
				.catch((error) => {
					console.error("Authentication failed", error);
					setAuthenticationError(error);
				})
				.finally(() => {
					setShowAuthLoader(false);
				});
		}
	}

	function handleChangeSwitchValue(key: string, checked: boolean) {
		setMaintenanceSettings((currentMaintenanceSettings) => ({
			...currentMaintenanceSettings,
			[key]: checked
		}));
	}

	function handleToggleTCPSettings(checked: boolean) {
		setMaintenanceSettings((currentMaintenanceSettings) => ({
			...currentMaintenanceSettings,
			enableTcp: checked,
			tcpAttachFrame: !checked ? false : currentMaintenanceSettings.tcpAttachFrame,
			tcpAttachPlate: !checked ? false : currentMaintenanceSettings.tcpAttachPlate
		}));
	}

	function handleChangeFuzzyMatchingThreshold(updatedValue: string) {
		setMaintenanceSettings((currentMaintenanceSettings) => ({
			...currentMaintenanceSettings,
			fuzzyMatchingThreshold: updatedValue
		}));
	}

	// function handleChangeProcessingDimensions(updatedValue: string, key: "processing_width" | "processing_height") {
	// 	setMaintenanceSettings((currentMaintenanceSettings) => ({
	// 		...currentMaintenanceSettings,
	// 		[key]: updatedValue
	// 	}));
	// }

	function handleChangeHttpWebhookUrl(updatedValue: string) {
		setMaintenanceSettings((currentMaintenanceSettings) => ({
			...currentMaintenanceSettings,
			httpWebhookUrl: updatedValue
		}));
	}

	// function handleTestWebhookConnection() {
	// 	if (!maintenanceSettings.httpWebhookUrl.trim()) {
	// 		dispatch(
	// 			showFeedbackNotification({
	// 				message: "Please enter a webhook URL first",
	// 				severity: "warning"
	// 			})
	// 		);
	// 		return;
	// 	}

	// 	setTestWebhookLoading(true);

	// 	// Create a test payload
	// 	const testPayload = {
	// 		event_type: "test_connection",
	// 		timestamp: new Date().toISOString(),
	// 		message: "This is a test webhook connection from GateGuard system",
	// 		status: "test"
	// 	};

	// 	// Convert custom headers to object
	// 	const customHeaders: { [key: string]: string } = {};
	// 	maintenanceSettings.webhookCustomHeaders.forEach((header) => {
	// 		if (header.key.trim() && header.value.trim()) {
	// 			customHeaders[header.key.trim()] = header.value.trim();
	// 		}
	// 	});

	// 	// Prepare headers
	// 	const headers: { [key: string]: string } = {
	// 		"Content-Type": "application/json",
	// 		...customHeaders
	// 	};

	// 	// Test the webhook
	// 	fetch(maintenanceSettings.httpWebhookUrl.trim(), {
	// 		method: "POST",
	// 		headers: headers,
	// 		body: JSON.stringify(testPayload)
	// 	})
	// 		.then((response) => {
	// 			if (response.ok) {
	// 				dispatch(
	// 					showFeedbackNotification({
	// 						message: "Webhook connection test successful!",
	// 						severity: "success"
	// 					})
	// 				);
	// 			} else {
	// 				dispatch(
	// 					showFeedbackNotification({
	// 						message: `Webhook test failed with status: ${response.status} ${response.statusText}`,
	// 						severity: "error"
	// 					})
	// 				);
	// 			}
	// 		})
	// 		.catch((error) => {
	// 			dispatch(
	// 				showFeedbackNotification({
	// 					message: `Webhook connection test failed: ${error.message}`,
	// 					severity: "error"
	// 				})
	// 			);
	// 		})
	// 		.finally(() => {
	// 			setTestWebhookLoading(false);
	// 		});
	// }

	function handleChangeProductName(updatedValue: string) {
		setMaintenanceSettings((currentMaintenanceSettings) => ({
			...currentMaintenanceSettings,
			productName: updatedValue
		}));
	}

	function handleChangeProtocol(updatedValue: "tcp" | "udp") {
		setMaintenanceSettings((currentMaintenanceSettings) => ({
			...currentMaintenanceSettings,
			protocol: updatedValue
		}));
	}

	function handleAddCustomHeader() {
		setMaintenanceSettings((currentSettings) => ({
			...currentSettings,
			webhookCustomHeaders: [...currentSettings.webhookCustomHeaders, { key: "", value: "" }]
		}));
	}

	function handleChangeCustomHeader(index: number, type: "key" | "value", changedText: string) {
		setMaintenanceSettings((currentSettings) => {
			if (index >= 0 && index < currentSettings.webhookCustomHeaders.length) {
				const updatedHeaders = [...currentSettings.webhookCustomHeaders];
				updatedHeaders[index][type] = changedText;
				return {
					...currentSettings,
					webhookCustomHeaders: updatedHeaders
				};
			}

			return currentSettings;
		});
	}

	function handleDeleteCustomHeader(index: number) {
		setMaintenanceSettings((currentSettings) => {
			const updatedCustomHeaders = [...currentSettings.webhookCustomHeaders];
			updatedCustomHeaders.splice(index, 1);
			return {
				...currentSettings,
				webhookCustomHeaders: updatedCustomHeaders
			};
		});
	}

	function handleSaveButtonClick() {
		if (siteConfig) {
			const customHeaders: { [key in string]: string } = {};

			for (const customHeaderItem of maintenanceSettings.webhookCustomHeaders) {
				customHeaders[customHeaderItem.key] = customHeaderItem.value;
			}

			updateSiteConfig({
				id: siteConfig.id,
				data: {
					camera_mode: maintenanceSettings.protocol === "tcp" ? CAMERA_MODE_PROTOCOL_E.TCP : CAMERA_MODE_PROTOCOL_E.UDP,
					filter_unreadable_plate_events: maintenanceSettings.filterUnreadableEvents,
					product_name: maintenanceSettings.productName,
					ml_settings: {
						allow_unauth_exit: maintenanceSettings.unauthorizedExit,
						auth_plate_matching_threshold: Number(maintenanceSettings.fuzzyMatchingThreshold),
						enable_manager_webhook: maintenanceSettings.enableHttpWebhook,
						draw_bbox: maintenanceSettings.drawBoundingBox,
						enable_auth_plate_mathching: maintenanceSettings.enableFuzzyMatching,
						manager_webhook_attach_frame: maintenanceSettings.webhookAttachFrame,
						manager_webhook_attach_plate: maintenanceSettings.webhookAttachPlate,
						manager_webhook_custom_header: customHeaders,
						manager_webhook_send_single_event: maintenanceSettings.sendOneEvent,
						manager_webhook_url: maintenanceSettings.httpWebhookUrl,
						radar_info: { ...siteConfig.ml_settings.radar_info },
						relay_setting: siteConfig.ml_settings.relay_setting,
						show_lc_counts_rtmp: false,
						show_processing_fps_rtmp: siteConfig.ml_settings.show_processing_fps_rtmp,
						enable_manager_tcp: maintenanceSettings.enableTcp,
						manager_tcp_attach_frame: maintenanceSettings.tcpAttachFrame,
						manager_tcp_attach_plate: maintenanceSettings.tcpAttachPlate,
						streammux_width: Number(maintenanceSettings.processing_width),
						streammux_height: Number(maintenanceSettings.processing_height)
					}
				}
			});
		}
	}

	useEffect(() => {
		if (
			RestrictedAccess.includes(RESTRICTED_ACCESS_E.ADDITIONAL_SETTINGS_PAGE) &&
			authState.user.token &&
			authState.user.token !== "invalid"
		) {
			navigate("/configuration");
		}
	}, [RestrictedAccess, authState.user.token, navigate]);

	useEffect(() => {
		if (resetSystemSuccess) {
			handleCloseLoaderDialog();
			dispatch(clearAuthState());
			resetResetSystemState();
			// dispatch(logoutUser());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resetSystemSuccess]);

	useEffect(() => {
		if (resetSystemError) {
			dispatch(
				showFeedbackNotification({
					message:
						"data" in resetSystemError && resetSystemError.data
							? String(resetSystemError.data)
							: "Failed to reset system. Please try again",
					severity: "error"
				})
			);
			resetResetSystemState();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resetSystemError]);

	useEffect(() => {
		if (siteConfig) {
			setMaintenanceSettings({
				drawBoundingBox: siteConfig.ml_settings.draw_bbox ?? false,
				showVehicleCount: siteConfig.ml_settings.show_lc_counts_rtmp ?? false,
				enableFuzzyMatching: siteConfig.ml_settings.enable_auth_plate_mathching ?? false,
				fuzzyMatchingThreshold: String(siteConfig.ml_settings.auth_plate_matching_threshold) ?? "",
				productName: siteConfig.product_name ?? "",
				enableHttpWebhook: siteConfig.ml_settings.enable_manager_webhook ?? false,
				httpWebhookUrl: siteConfig.ml_settings.manager_webhook_url ?? "",
				sendOneEvent: siteConfig.ml_settings.manager_webhook_send_single_event ?? false,
				protocol: siteConfig.camera_mode === CAMERA_MODE_PROTOCOL_E.TCP ? "tcp" : "udp",
				unauthorizedExit: siteConfig.ml_settings.allow_unauth_exit ?? false,
				filterUnreadableEvents: siteConfig.filter_unreadable_plate_events ?? false,
				webhookAttachFrame: siteConfig.ml_settings.manager_webhook_attach_frame ?? false,
				webhookAttachPlate: siteConfig.ml_settings.manager_webhook_attach_plate ?? false,
				webhookCustomHeaders: siteConfig.ml_settings.manager_webhook_custom_header
					? Object.entries(siteConfig.ml_settings.manager_webhook_custom_header).map(([key, value]) => ({ key, value }))
					: [],
				enableTcp: siteConfig.ml_settings.enable_manager_tcp || false,
				tcpAttachFrame: siteConfig.ml_settings.manager_tcp_attach_frame || false,
				tcpAttachPlate: siteConfig.ml_settings.manager_tcp_attach_plate || false,
				client_id: clientDetails && clientDetails.client_id ? String(clientDetails.client_id) : "",
				client_name: clientDetails?.name ?? "",
				node_id: nodeDetails && nodeDetails.node_id ? String(nodeDetails.node_id) : "",
				node_name: nodeDetails?.username ?? "",
				processing_width: siteConfig.ml_settings.streammux_width ? String(siteConfig.ml_settings.streammux_width) : "",
				processing_height: siteConfig.ml_settings.streammux_height
					? String(siteConfig.ml_settings.streammux_height)
					: ""
			});
		}
	}, [clientDetails, nodeDetails, siteConfig]);

	useEffect(() => {
		if (updateSiteConfigSuccess) {
			resetUpdateSiteConfigState();
			refetchSiteConfig();
		}
	}, [refetchSiteConfig, resetUpdateSiteConfigState, updateSiteConfigSuccess]);

	useEffect(() => {
		if (rebootInferenceError) {
			handleCloseLoaderDialog();
			dispatch(
				showFeedbackNotification({
					message:
						"data" in rebootInferenceError && rebootInferenceError.data
							? String(rebootInferenceError.data)
							: "Failed to reboot inference engine. Please try again.",
					severity: "error"
				})
			);
			resetRebootInferenceState();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rebootInferenceError]);

	useEffect(() => {
		if (rebootInferenceSuccess) {
			handleCloseLoaderDialog();
			dispatch(
				showFeedbackNotification({
					message: "Inference system restarted successfully",
					severity: "success"
				})
			);
			resetRebootInferenceState();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rebootInferenceSuccess]);

	useEffect(() => {
		if (updateSiteConfigError) {
			dispatch(
				showFeedbackNotification({
					message:
						"data" in updateSiteConfigError && updateSiteConfigError.data
							? String(updateSiteConfigError.data)
							: "Failed to update settings. Please try again.",
					severity: "error"
				})
			);

			resetUpdateSiteConfigState();
		}
	}, [dispatch, resetUpdateSiteConfigState, updateSiteConfigError]);

	useEffect(() => {
		if (forceSyncResponse) {
			startSyncStatusPolling();
			resetForceSyncState();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [forceSyncResponse]);

	useEffect(() => {
		if (forceSyncError) {
			dispatch(
				showFeedbackNotification({
					message: "data" in forceSyncError && forceSyncError.data ? String(forceSyncError.data) : "Sync failed",
					severity: "error"
				})
			);

			resetForceSyncState();
			handleCloseConfirmationDialog();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [forceSyncError]);

	return (
		<Box className="configuration-screen-wrapper flex-wrapper">
			<Box
				className="system-settings-section-wrapper"
				sx={{
					background: "#ffffff",
					padding: { xs: "1rem", sm: "1.5rem" },
					borderRadius: "12px",
					boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
					border: "1px solid rgba(0, 0, 0, 0.06)"
				}}
			>
				<Box className="maintenance-screen-section" sx={{ marginBottom: "1rem" }}>
					<Box>
						<Typography variant="h6" color="var(--color-primary-main)">
							System Settings
						</Typography>

						<Typography variant="body1" color="#616161">
							Adjust system functionality settings
						</Typography>
					</Box>
				</Box>

				<Box className="maintenance-screen-section">
					<Grid container spacing={2} className="maintenance-grid-container">
						<Grid item xs={12} md={6}>
							<Box className="maintenance-setting-wrapper">
								<Box className="maintenance-setting-text-wrapper">
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										Drawing Box Over Number Plate
									</Typography>

									<Typography variant="body2" color="#616161">
										Let system draw box on the Number plate
									</Typography>
								</Box>

								<Switch
									checked={maintenanceSettings.drawBoundingBox}
									onChange={(_, checked) => handleChangeSwitchValue("drawBoundingBox", checked)}
								/>
							</Box>
						</Grid>

						<Grid item xs={12} md={6}>
							<Box className="maintenance-setting-wrapper">
								<Box className="maintenance-setting-text-wrapper">
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										Send Only One Event
									</Typography>

									<Typography variant="body2" color="#616161">
										*Disabling this option may reduce accuracy
									</Typography>
								</Box>

								<Switch
									checked={maintenanceSettings.sendOneEvent}
									onChange={(_, checked) => handleChangeSwitchValue("sendOneEvent", checked)}
								/>
							</Box>
						</Grid>

						{/* <Grid item xs={12} md={6}>
						<Box className="maintenance-setting-wrapper">
							<Box className="maintenance-setting-text-wrapper">
								<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
									Drawing Box over vehicle
								</Typography>

								<Typography variant="body2" color="#616161">
									Let system draw box on the vehicle
								</Typography>
							</Box>

							<Switch
								checked={maintenanceSettings.drawVehicleBox}
								onChange={(_, checked) => handleChangeSwitchValue("drawVehicleBox", checked)}
							/>
						</Box>
					</Grid> */}

						{/* <Grid item xs={12} md={6}>
						<Box className="maintenance-setting-wrapper">
							<Box className="maintenance-setting-text-wrapper">
								<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
									Vehicle Count On Top Right
								</Typography>

								<Typography variant="body2" color="#616161">
									Display vehicle count on top right of the screen
								</Typography>
							</Box>

							<Switch
								checked={maintenanceSettings.showVehicleCount}
								onChange={(_, checked) => handleChangeSwitchValue("showVehicleCount", checked)}
							/>
						</Box>
					</Grid> */}

						<Grid item xs={12} md={6}>
							<Box className="maintenance-setting-wrapper">
								<Box className="maintenance-setting-text-wrapper">
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										Fuzzy Matching
									</Typography>

									<Typography variant="body2" color="#616161">
										Enable fuzzy matching of number plates
									</Typography>
								</Box>

								<Switch
									checked={maintenanceSettings.enableFuzzyMatching}
									onChange={(_, checked) => handleChangeSwitchValue("enableFuzzyMatching", checked)}
								/>
							</Box>
						</Grid>

						<Grid item xs={12} md={6}>
							<Box className="maintenance-setting-wrapper">
								<Box className="maintenance-setting-text-wrapper">
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										Fuzzy Matching Threshold
									</Typography>

									<Typography variant="body2" color="#616161">
										Fixate the threshold for Fuzzy matching.
									</Typography>
								</Box>

								<NumberInput
									size="small"
									variant="filled"
									placeholder="Value"
									disabled={!maintenanceSettings.enableFuzzyMatching}
									InputProps={{ disableUnderline: true, hiddenLabel: true, classes: { root: "filled-input-root" } }}
									value={maintenanceSettings.fuzzyMatchingThreshold}
									onChange={(event) => handleChangeFuzzyMatchingThreshold(event.target.value)}
								/>
							</Box>
						</Grid>

						<Grid item xs={12} md={6}>
							<Box className="maintenance-setting-wrapper">
								<Box className="maintenance-setting-text-wrapper">
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										Product name
									</Typography>

									<Typography variant="body2" color="#616161">
										Provide the product name
									</Typography>
								</Box>

								<TextField
									size="small"
									variant="filled"
									placeholder="Enter name here"
									sx={{ flexGrow: 1 }}
									InputProps={{ disableUnderline: true, hiddenLabel: true, classes: { root: "filled-input-root" } }}
									value={maintenanceSettings.productName}
									onChange={(event) => handleChangeProductName(event.target.value)}
								/>
							</Box>
						</Grid>

						<Grid item xs={12} md={6}>
							<Box className="maintenance-setting-wrapper">
								<Box className="maintenance-setting-text-wrapper">
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										Camera Protocol
									</Typography>

									<Typography variant="body2" color="#616161">
										Provide the protocol to use
									</Typography>
								</Box>

								<FormControl>
									<RadioGroup
										row
										sx={{ gap: "1rem" }}
										value={maintenanceSettings.protocol}
										onChange={(_, value) => handleChangeProtocol(value as "tcp" | "udp")}
									>
										<FormControlLabel value="tcp" control={<Radio size="small" />} label="TCP" />
										<FormControlLabel value="udp" control={<Radio size="small" />} label="UDP" />
									</RadioGroup>
								</FormControl>
							</Box>
						</Grid>

						<Grid item xs={12} md={6}>
							<Box className="maintenance-setting-wrapper">
								<Box className="maintenance-setting-text-wrapper">
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										Unauthorized Exit
									</Typography>

									<Typography variant="body2" color="#616161">
										Enabling will allow barrier to open for unauthorized exits
									</Typography>
								</Box>

								<Switch
									checked={maintenanceSettings.unauthorizedExit}
									onChange={(_, checked) => handleChangeSwitchValue("unauthorizedExit", checked)}
								/>
							</Box>
						</Grid>

						<Grid item xs={12} md={6}>
							<Box className="maintenance-setting-wrapper">
								<Box className="maintenance-setting-text-wrapper">
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										Filter Unreadable Plate Events
									</Typography>

									<Typography variant="body2" color="#616161">
										Enabling will hide the unreadable number plate events
									</Typography>
								</Box>

								<Switch
									checked={maintenanceSettings.filterUnreadableEvents}
									onChange={(_, checked) => handleChangeSwitchValue("filterUnreadableEvents", checked)}
								/>
							</Box>
						</Grid>

						{/* <Grid item xs={12} md={6}>
						<Box className="maintenance-setting-wrapper">
							<Box className="maintenance-setting-text-wrapper">
								<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
									Processing Width
								</Typography>

								<Typography variant="body2" color="#616161">
									Provide the width of the processing video.
								</Typography>
							</Box>

							<NumberInput
								size="small"
								variant="filled"
								placeholder="Value"
								InputProps={{ disableUnderline: true, hiddenLabel: true, classes: { root: "filled-input-root" } }}
								value={maintenanceSettings.processing_width}
								onChange={(event) => handleChangeProcessingDimensions(event.target.value, "processing_width")}
							/>
						</Box>
					</Grid> */}

						{/* <Grid item xs={12} md={6}>
						<Box className="maintenance-setting-wrapper">
							<Box className="maintenance-setting-text-wrapper">
								<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
									Processing Height
								</Typography>

								<Typography variant="body2" color="#616161">
									Provide the height of the processing video.
								</Typography>
							</Box>

							<NumberInput
								size="small"
								variant="filled"
								placeholder="Value"
								InputProps={{ disableUnderline: true, hiddenLabel: true, classes: { root: "filled-input-root" } }}
								value={maintenanceSettings.processing_height}
								onChange={(event) => handleChangeProcessingDimensions(event.target.value, "processing_height")}
							/>
						</Box>
					</Grid> */}
					</Grid>
				</Box>

				<Divider flexItem sx={{ margin: "1.5rem 0 1rem" }} />

				<Box className="maintenance-screen-section" sx={{ marginBottom: "1rem" }}>
					<Box>
						<Typography variant="h6" color="var(--color-primary-main)">
							HTTP Webhook Settings
						</Typography>

						<Typography variant="body1" color="#616161">
							Adjust HTTP Manager webhook settings
						</Typography>
					</Box>
				</Box>

				<Box className="maintenance-screen-section">
					<Grid container spacing={2} className="maintenance-grid-container">
						<Grid item xs={12} md={6}>
							<Box className="maintenance-setting-wrapper">
								<Box className="maintenance-setting-text-wrapper">
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										HTTP Webhook
									</Typography>

									<Typography variant="body2" color="#616161">
										Allow webhooks on the network
									</Typography>
								</Box>

								<Switch
									checked={maintenanceSettings.enableHttpWebhook}
									onChange={(_, checked) => handleChangeSwitchValue("enableHttpWebhook", checked)}
								/>
							</Box>
						</Grid>

						<Grid item xs={12} md={6}>
							<Box className="maintenance-setting-wrapper">
								<Box className="maintenance-setting-text-wrapper">
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										HTTP Webhook URL
									</Typography>

									<Typography variant="body2" color="#616161">
										Provide URL of the webhook
									</Typography>
								</Box>

								<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1 }}>
									<TextField
										size="small"
										variant="filled"
										placeholder="Enter URL here"
										disabled={!maintenanceSettings.enableHttpWebhook}
										InputProps={{ disableUnderline: true, hiddenLabel: true, classes: { root: "filled-input-root" } }}
										sx={{ flexGrow: 1 }}
										value={maintenanceSettings.httpWebhookUrl}
										onChange={(event) => handleChangeHttpWebhookUrl(event.target.value)}
										autoComplete="off"
									/>

									{/* <Tooltip title="Test webhook connection">
										<IconButton
											color="primary"
											onClick={handleTestWebhookConnection}
											disabled={
												!maintenanceSettings.enableHttpWebhook ||
												!maintenanceSettings.httpWebhookUrl.trim() ||
												testWebhookLoading
											}
											sx={{
												width: 40,
												height: 40,
												borderRadius: "8px",
												"&:hover": {
													backgroundColor: "rgba(91, 78, 191, 0.08)"
												}
											}}
										>
											{testWebhookLoading ? <CircularProgress size={20} /> : <SyncIcon fontSize="small" />}
										</IconButton>
									</Tooltip> */}
								</Box>
							</Box>
						</Grid>

						<Grid item xs={12} md={6}>
							<Box className="maintenance-setting-wrapper">
								<Box className="maintenance-setting-text-wrapper">
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										Attach Frame
									</Typography>

									<Typography variant="body2" color="#616161">
										Attach evidence images in webhook communication
									</Typography>
								</Box>

								<Switch
									disabled={!maintenanceSettings.enableHttpWebhook}
									checked={maintenanceSettings.webhookAttachFrame}
									onChange={(_, checked) => handleChangeSwitchValue("webhookAttachFrame", checked)}
								/>
							</Box>
						</Grid>

						<Grid item xs={12} md={6}>
							<Box className="maintenance-setting-wrapper">
								<Box className="maintenance-setting-text-wrapper">
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										Attach Plate
									</Typography>

									<Typography variant="body2" color="#616161">
										Attach plate images in webhook communication
									</Typography>
								</Box>

								<Switch
									disabled={!maintenanceSettings.enableHttpWebhook}
									checked={maintenanceSettings.webhookAttachPlate}
									onChange={(_, checked) => handleChangeSwitchValue("webhookAttachPlate", checked)}
								/>
							</Box>
						</Grid>

						{maintenanceSettings.webhookCustomHeaders.map((customHeaderItem, index) => (
							<Grid item xs={12} md={6} key={index}>
								<Box className="maintenance-setting-wrapper" sx={{ justifyContent: "flex-start" }}>
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										Custom Header
									</Typography>

									<IconButton color="error" onClick={() => handleDeleteCustomHeader(index)} tabIndex={-1}>
										<RemoveCircleOutlineIcon />
									</IconButton>
								</Box>

								<Box
									className="maintenance-setting-wrapper maintenance-custom-header-wrapper"
									sx={{ alignItems: "flex-end" }}
								>
									<Box sx={{ flex: 1 }}>
										<InputLabel>Key</InputLabel>
										<TextField
											fullWidth
											size="small"
											variant="filled"
											placeholder="Enter key here"
											InputProps={{ disableUnderline: true, hiddenLabel: true, classes: { root: "filled-input-root" } }}
											value={customHeaderItem.key}
											onChange={(event) => handleChangeCustomHeader(index, "key", event.target.value)}
											autoComplete="off"
											disabled={!maintenanceSettings.enableHttpWebhook}
										/>
									</Box>

									<Box sx={{ flex: 1 }}>
										<InputLabel>Value</InputLabel>
										<TextField
											fullWidth
											size="small"
											variant="filled"
											placeholder="Enter value here"
											InputProps={{ disableUnderline: true, hiddenLabel: true, classes: { root: "filled-input-root" } }}
											value={customHeaderItem.value}
											onChange={(event) => handleChangeCustomHeader(index, "value", event.target.value)}
											autoComplete="off"
											disabled={!maintenanceSettings.enableHttpWebhook}
										/>
									</Box>
								</Box>
							</Grid>
						))}

						<Grid item xs={12}>
							<Box className="maintenance-setting-wrapper" sx={{ height: "100%", alignItems: "flex-end" }}>
								<Button
									disableElevation
									variant="contained"
									color="primary"
									startIcon={<AddIcon />}
									onClick={handleAddCustomHeader}
									disabled={!maintenanceSettings.enableHttpWebhook}
								>
									Add Custom Header
								</Button>
							</Box>
						</Grid>
					</Grid>
				</Box>

				<Divider flexItem sx={{ margin: "1.5rem 0 1rem" }} />

				<Box className="maintenance-screen-section" sx={{ marginBottom: "1rem" }}>
					<Box>
						<Typography variant="h6" color="var(--color-primary-main)">
							TCP Socket Settings
						</Typography>

						<Typography variant="body1" color="#616161">
							Adjust TCP socket settings
						</Typography>
					</Box>
				</Box>

				<Box className="maintenance-screen-section" sx={{ width: "100%" }}>
					<Grid container spacing={2} className="maintenance-grid-container">
						<Grid item xs={12} md={6}>
							<Box className="maintenance-setting-wrapper">
								<Box className="maintenance-setting-text-wrapper">
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										TCP Socket
									</Typography>

									<Typography variant="body2" color="#616161">
										Allow TCP socket communication
									</Typography>
								</Box>

								<Switch
									checked={maintenanceSettings.enableTcp}
									onChange={(_, checked) => handleToggleTCPSettings(checked)}
								/>
							</Box>
						</Grid>

						<Grid item xs={12} md={6}>
							<Box className="maintenance-setting-wrapper">
								<Box className="maintenance-setting-text-wrapper">
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										Attach Frame
									</Typography>

									<Typography variant="body2" color="#616161">
										Attach evidence images in TCP communication
									</Typography>
								</Box>

								<Switch
									checked={maintenanceSettings.tcpAttachFrame}
									disabled={!maintenanceSettings.enableTcp}
									onChange={(_, checked) => handleChangeSwitchValue("tcpAttachFrame", checked)}
								/>
							</Box>
						</Grid>

						<Grid item xs={12} md={6}>
							<Box className="maintenance-setting-wrapper">
								<Box className="maintenance-setting-text-wrapper">
									<Typography variant="subtitle1" color="var(--color-primary-main)" fontWeight={500} fontSize="18px">
										Attach Plate
									</Typography>

									<Typography variant="body2" color="#616161">
										Attach plate images in TCP communication
									</Typography>
								</Box>

								<Switch
									checked={maintenanceSettings.tcpAttachPlate}
									disabled={!maintenanceSettings.enableTcp}
									onChange={(_, checked) => handleChangeSwitchValue("tcpAttachPlate", checked)}
								/>
							</Box>
						</Grid>
					</Grid>
				</Box>

				<Divider flexItem sx={{ margin: "1.5rem 0 1rem" }} />

				<Box className="maintenance-screen-section" sx={{ marginBottom: "1rem" }}>
					<Box className="maintenance-section-header" sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
						<Box>
							<Typography variant="h6" color="var(--color-primary-main)">
								Client and Node Settings
							</Typography>

							<Typography variant="body1" color="#616161">
								Adjust Client and Node settings
							</Typography>
						</Box>

						{getClientsListResponse && getNodesListResponse ? (
							<IconButton onClick={openClientNodeDetailsDialog}>
								<EditIcon />
							</IconButton>
						) : null}
					</Box>
				</Box>

				<Box className="maintenance-screen-section" sx={{ width: "100%" }}>
					{getClientsListLoading || getClientsListFetching || getNodesListLoading || getNodesListFetching ? (
						<Box
							sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
						>
							<CircularProgress />
						</Box>
					) : (
						<Grid container spacing={2} className="maintenance-client-node-grid">
							<Grid item xs={12} md={3}>
								<InputLabel
									htmlFor="client-id-input"
									sx={{ color: "#424242", fontWeight: 500, fontSize: "18px", marginBottom: "0.5rem" }}
								>
									Client ID
								</InputLabel>

								<TextField
									fullWidth
									size="small"
									variant="filled"
									placeholder="Enter client ID here"
									id="client-id-input"
									InputProps={{
										disableUnderline: true,
										hiddenLabel: true,
										classes: { root: "filled-input-root" },
										readOnly: true
									}}
									sx={{ flexGrow: 1 }}
									value={maintenanceSettings.client_id}
									autoComplete="off"
								/>
							</Grid>

							<Grid item xs={12} md={3}>
								<InputLabel
									htmlFor="client-name-input"
									sx={{ color: "#424242", fontWeight: 500, fontSize: "18px", marginBottom: "0.5rem" }}
								>
									Client Name
								</InputLabel>

								<TextField
									fullWidth
									size="small"
									variant="filled"
									placeholder="Enter client name here"
									id="client-name-input"
									InputProps={{
										disableUnderline: true,
										hiddenLabel: true,
										classes: { root: "filled-input-root" },
										readOnly: true
									}}
									sx={{ flexGrow: 1 }}
									value={maintenanceSettings.client_name}
									autoComplete="off"
								/>
							</Grid>

							<Grid item xs={12} md={3}>
								<InputLabel
									htmlFor="node-id-input"
									sx={{ color: "#424242", fontWeight: 500, fontSize: "18px", marginBottom: "0.5rem" }}
								>
									Node ID
								</InputLabel>

								<TextField
									fullWidth
									size="small"
									variant="filled"
									placeholder="Enter node ID here"
									id="node-id-input"
									InputProps={{
										disableUnderline: true,
										hiddenLabel: true,
										classes: { root: "filled-input-root" },
										readOnly: true
									}}
									sx={{ flexGrow: 1 }}
									value={maintenanceSettings.node_id}
									autoComplete="off"
								/>
							</Grid>

							<Grid item xs={12} md={3}>
								<InputLabel
									htmlFor="node-name-input"
									sx={{ color: "#424242", fontWeight: 500, fontSize: "18px", marginBottom: "0.5rem" }}
								>
									Node Name
								</InputLabel>

								<TextField
									fullWidth
									size="small"
									variant="filled"
									placeholder="Enter node name here"
									id="node-name-input"
									InputProps={{
										disableUnderline: true,
										hiddenLabel: true,
										classes: { root: "filled-input-root" },
										readOnly: true
									}}
									sx={{ flexGrow: 1 }}
									value={maintenanceSettings.node_name}
									autoComplete="off"
								/>
							</Grid>
						</Grid>
					)}
				</Box>

				<Divider flexItem sx={{ margin: "1.5rem 0 1rem" }} />

				<Box className="maintenance-screen-section">
					<Typography variant="h6" color="var(--color-primary-main)">
						Reboot
					</Typography>

					<Box className="maintenance-action-buttons" sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
						<Button
							disableElevation
							variant="contained"
							color="primary"
							startIcon={<RestartAltIcon />}
							onClick={() => handleOpenConfirmationDialog("reboot_device")}
						>
							Restart System
						</Button>

						<Button
							disableElevation
							variant="contained"
							color="primary"
							startIcon={<RestartAltIcon />}
							onClick={() => handleOpenConfirmationDialog("reboot_inference")}
						>
							Restart Inference System
						</Button>
					</Box>
				</Box>

				{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.RESET_DEVICE) ? (
					<>
						<Divider flexItem sx={{ margin: "1.5rem 0 1rem" }} />

						<Box className="maintenance-screen-section">
							<Typography variant="h6" color="var(--color-primary-main)">
								Reset
							</Typography>

							<Box className="maintenance-action-buttons">
								<Button
									variant="outlined"
									color="primary"
									startIcon={<AutoDeleteIcon />}
									onClick={() => handleOpenConfirmationDialog("reset")}
									sx={{ paddingX: 3 }}
								>
									Reset System
								</Button>
							</Box>
						</Box>
					</>
				) : null}

				{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.FORCE_SYNC) && siteConfig?.enable_gg_connect ? (
					<>
						<Divider flexItem sx={{ margin: "1.5rem 0 1rem" }} />

						<Box className="maintenance-screen-section">
							<Typography variant="h6" color="var(--color-primary-main)">
								Sync
							</Typography>

							<Box className="maintenance-action-buttons">
								<LoadingButton
									variant="outlined"
									color="primary"
									startIcon={<SyncIcon />}
									onClick={() => handleOpenConfirmationDialog("force_sync")}
									sx={{ paddingX: 3 }}
									loading={forceSyncLoading || forceSyncStatusLoading}
									loadingPosition="start"
								>
									{forceSyncLoading || forceSyncStatusLoading ? "Sync in progress..." : "Force Sync"}
								</LoadingButton>
							</Box>
						</Box>
					</>
				) : null}
			</Box>

			{maintenanceSettingsChanged ? (
				<Box
					// className="maintenance-sticky-save"
					sx={{
						position: "sticky",
						bottom: 0,
						left: 0,
						right: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "flex-end",
						padding: { xs: "1rem", sm: "2rem 1rem" },
						background: "rgba(255, 255, 255, 0.8)",
						backdropFilter: "blur(20px)",
						WebkitBackdropFilter: "blur(20px)",
						border: "1px solid rgba(255, 255, 255, 0.3)",
						borderRadius: "16px",
						zIndex: 999,
						marginTop: "auto",
						boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
						"&::before": {
							content: '""',
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							borderRadius: "16px",
							padding: "1px",
							background: "linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.1) 100%)",
							mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
							maskComposite: "exclude",
							WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
							WebkitMaskComposite: "xor"
						}
					}}
				>
					<LoadingButton
						variant="contained"
						color="success"
						startIcon={<SaveIcon />}
						onClick={handleSaveButtonClick}
						loading={updateSiteConfigLoading}
						loadingPosition="start"
					>
						Save Changes
					</LoadingButton>
				</Box>
			) : null}

			<ConfirmationDialog
				width="sm"
				open={!!confirmationDialogType}
				title={
					confirmationDialogType === "reboot_device"
						? "Reboot System"
						: confirmationDialogType === "reboot_inference"
						? "Reboot Inference System"
						: confirmationDialogType === "reset"
						? "Reset System"
						: confirmationDialogType === "force_sync"
						? "Force Sync"
						: ""
				}
				heading={`Are you sure you want to ${
					confirmationDialogType === "reboot_device"
						? "reboot the system"
						: confirmationDialogType === "reboot_inference"
						? "reboot the inference system"
						: confirmationDialogType === "reset"
						? "reset the system"
						: confirmationDialogType === "force_sync"
						? "force sync"
						: ""
				}?`}
				warning={confirmationDialogType === "reset" ? "This will erase all the data stored on the system." : ""}
				onClose={handleCloseConfirmationDialog}
				onConfirm={handleConfirmAction}
			/>

			<Dialog open={!!loaderDialogMessage} fullWidth maxWidth="sm">
				<DialogContent className="maintenance-dialog-content">
					<Box
						sx={{
							width: "100%",
							height: "100%",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							gap: "1rem"
						}}
					>
						<CircularProgress size={32} />

						<Typography>{loaderDialogMessage}</Typography>
					</Box>
				</DialogContent>
			</Dialog>

			<Dialog open={showResetAuthDialog} onClose={handleCloseResetAuthDialog} fullWidth maxWidth="sm">
				<Box component="form" noValidate onSubmit={handleSubmit(handleSubmitAuthForm)}>
					<DialogTitle textAlign="center" color="white" classes={{ root: `dialog-title-root primary` }}>
						Authenticate
						<Box className="close-dialog-icon-wrapper">
							<IconButton color="inherit" onClick={handleCloseResetAuthDialog}>
								<CloseIcon color="inherit" />
							</IconButton>
						</Box>
					</DialogTitle>

					<DialogContent classes={{ root: "dialog-content-root maintenance-dialog-content" }}>
						<TextField
							required
							fullWidth
							autoFocus={!navigator.userAgent.match(/Android|webOS|iPhone|iPod|Blackberry/i)}
							type="password"
							autoComplete="new-password"
							variant="outlined"
							color="primary"
							label="Password"
							error={!!errors.password}
							helperText={errors.password && errors.password.message ? errors.password.message.toString() : ""}
							{...register("password", { required: "Password is required" })}
						/>

						{authenticationError ? (
							<Grid item xs={12} sx={{ marginTop: "1rem" }}>
								<Typography variant="body2" color="var(--color-error-main)" textAlign="center">
									{authenticationError}
								</Typography>
							</Grid>
						) : null}
					</DialogContent>

					<DialogActions sx={{ justifyContent: "center" }}>
						<LoadingButton variant="contained" color="primary" type="submit" loading={showAuthLoader}>
							Proceed
						</LoadingButton>
					</DialogActions>
				</Box>
			</Dialog>

			<ClientNodeDetailsDialog
				open={showClientNodeDetailsDialog}
				onClose={closeClientNodeDetailsDialog}
				clientNodeDetails={clientNodeDetails}
				createdClientId={
					getClientsListResponse && getClientsListResponse.length > 0 ? getClientsListResponse[0].id : -1
				}
				createdNodeId={getNodesListResponse && getNodesListResponse.length > 0 ? getNodesListResponse[0].id : -1}
			/>
		</Box>
	);
};

export default Maintenance;
